import React, {
	useCallback,
	useMemo,
	useState,
	type ChangeEvent,
	type MouseEvent,
	useEffect,
	useRef,
	type KeyboardEventHandler,
	type ClipboardEventHandler,
	type ReactElement,
} from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { ErrorMessage } from '@atlaskit/form/Messages';
import { Inline, xcss } from '@atlaskit/primitives';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { TEXT_AREA_MAX_HEIGHT } from './constants.tsx';
import messages from './messages.tsx';

/**
 * The properties of the basic textarea component
 */
type Props = {
	/**
	 * The value to be displayed in the textarea.
	 */
	value: string;
	/**
	 * An optional function that accepts a string and returns an error message.
	 * The update button is disabled if this method returns anything other than undefined.
	 */
	getErrorMessage?: (inputValue: string) => React.ReactElement | string | undefined;
	/**
	 * A callback triggered when the user clicks the Update button to submit the changes.
	 */
	onChange: (inputValue: string) => void;
	/**
	 * An optional callback that is triggered for every change made by the user.
	 */
	onValueChange?: (inputValue: string) => void;
	/**
	 * An optional placeholder text to be displayed to the user. If not specified, a default placeholder text will be used.
	 */
	placeholderText?: string;
	/**
	 * Function used to set initial focus if textarea is used inside a popup.
	 */
	setInitialFocusRef?: (elem: HTMLElement | null) => void;
	/**
	 * Allows the textarea to be multiline. defaults to true.
	 */
	multiline?: boolean;
	/**
	 * An optional icon/element to be displayed in the right side of the footer.
	 */
	footerIcon?: ReactElement;
};

export const TextAreaView = ({
	value,
	getErrorMessage,
	onChange,
	onValueChange,
	placeholderText,
	setInitialFocusRef,
	multiline = true,
	footerIcon,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [unsubmittedValue, setUnsubmittedValue] = useState(value ?? '');

	const textAreaRef = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		if (setInitialFocusRef) {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
			setInitialFocusRef(textAreaRef.current);
		}
	}, [setInitialFocusRef]);

	const handleOnChange = useCallback(
		({ target: { value: targetValue } }: ChangeEvent<HTMLTextAreaElement>) => {
			if (onValueChange) onValueChange(targetValue);
			setUnsubmittedValue(targetValue);
		},
		[setUnsubmittedValue, onValueChange],
	);

	const handleOnSubmit = useCallback(
		(e: MouseEvent) => {
			e.preventDefault();
			onChange(unsubmittedValue);
		},
		[unsubmittedValue, onChange],
	);

	const hasError = useMemo(
		() => !!getErrorMessage?.(unsubmittedValue),
		[getErrorMessage, unsubmittedValue],
	);

	const handleKeyDown = useCallback<KeyboardEventHandler<HTMLTextAreaElement>>(
		(event) => {
			if (!multiline && event.key === 'Enter') {
				event.preventDefault();
			}
		},
		[multiline],
	);

	const handleOnPaste = useCallback<ClipboardEventHandler<HTMLTextAreaElement>>(
		(event) => {
			if (!multiline) {
				const data = event.clipboardData.getData('text/plain');

				if (textAreaRef.current && data.includes('\n')) {
					// Replace newlines with spaces
					const paste = data.replace(/\n/g, ' ');

					setUnsubmittedValue(
						unsubmittedValue.slice(0, textAreaRef.current.selectionStart) +
							paste +
							unsubmittedValue.slice(textAreaRef.current.selectionEnd),
					);

					event.preventDefault();
				}
			}
		},
		[multiline, unsubmittedValue],
	);

	return (
		<DialogInner>
			<TextAreaWrapper>
				<TextArea
					ref={textAreaRef}
					onChange={handleOnChange}
					value={unsubmittedValue}
					onKeyDown={handleKeyDown}
					aria-multiline={multiline}
					maxHeight={TEXT_AREA_MAX_HEIGHT}
					onPaste={handleOnPaste}
					appearance="none"
					minimumRows={4}
					placeholder={placeholderText ?? formatMessage(messages.textAreaPlaceholder)}
					testId="jql-builder-basic-textarea.ui.text-area"
				/>
			</TextAreaWrapper>
			{hasError && (
				<ErrorMessageWrapper>
					<ErrorMessage>{getErrorMessage?.(unsubmittedValue)}</ErrorMessage>
				</ErrorMessageWrapper>
			)}
			<Inline spread="space-between" alignBlock="center" grow="fill" xcss={footerStyles}>
				<ButtonWrapper>
					<Button
						appearance="primary"
						onClick={handleOnSubmit}
						type="submit"
						isDisabled={hasError}
						testId="jql-builder-basic-textarea.ui.update-button"
					>
						{formatMessage(messages.updateButtonText)}
					</Button>
				</ButtonWrapper>
				{footerIcon}
			</Inline>
		</DialogInner>
	);
};

export default TextAreaView;

const footerStyles = xcss({
	paddingRight: 'space.150',
	borderTop: `${token('color.border')} solid ${token('border.width.selected')}`,
	paddingTop: 'space.075',
	paddingBottom: 'space.075',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div({
	marginTop: token('space.0'),
	marginRight: token('space.150'),
	marginBottom: token('space.0'),
	marginLeft: token('space.150'),
	maxWidth: `calc(100% - ${token('space.300')})`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextAreaWrapper = styled.div({
	marginTop: `${token('space.025')}`,
	marginBottom: `${token('space.075')}`,
	marginLeft: `${token('space.075')}`,
	marginRight: `${token('space.075')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: TEXT_AREA_MAX_HEIGHT,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorMessageWrapper = styled.div({
	marginTop: token('space.050'),
	marginRight: token('space.150'),
	marginBottom: token('space.050'),
	marginLeft: token('space.150'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DialogInner = styled.div({
	width: '300px',
});
